import { AlertTriangle } from 'lucide-react';

interface TaxBreakdownProps {
  /** Pre-tax subtotal. */
  subtotal: number;
  /** 2-letter US state code (e.g. "TX"). */
  taxState?: string;
  /** Tax rate applied to labor as a decimal (e.g. 0.0825 for 8.25%). */
  laborTaxRate?: number;
  /** Tax rate applied to materials; 0 when the state does not tax them. Omit when unknown. */
  materialsTaxRate?: number;
  /** Materials portion of the subtotal; enables the labor / materials rate split. */
  materialsCost?: number;
  /** Calculated tax amount in dollars. */
  taxAmount?: number;
  /** Tax-inclusive total. Falls back to subtotal when undefined. */
  totalWithTax?: number;
  /** True for interstate moves requiring manual tax determination. */
  requiresManualTaxReview?: boolean;
  /** Currency symbol (default "$"). */
  symbol?: string;
  /** Visual variant. "card" renders in a white bordered card. "inline" renders rows directly. */
  variant?: 'card' | 'inline';
}

function fmtCurrency(n: number, sym: string): string {
  return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Reusable tax breakdown component.
 *
 * Shows subtotal, tax rate (with state), tax amount, and total.
 * Handles manual review (interstate) and zero-tax states.
 * Only renders when tax data exists — caller should gate on
 * `laborTaxRate != null || requiresManualTaxReview`.
 */
export function TaxBreakdown({
  subtotal,
  taxState,
  laborTaxRate,
  materialsTaxRate,
  materialsCost,
  taxAmount,
  totalWithTax,
  requiresManualTaxReview,
  symbol = '$',
  variant = 'inline',
}: TaxBreakdownProps) {
  const total = totalWithTax ?? subtotal;
  const laborRate = laborTaxRate ?? 0;
  const matRate = materialsTaxRate ?? laborRate;
  // Show two rate rows only when materials exist and are taxed differently from labor.
  const splitRates = (materialsCost ?? 0) > 0 && materialsTaxRate != null && materialsTaxRate !== laborRate;
  const noTax = laborRate === 0 && (!splitRates || matRate === 0);
  const pct = (r: number) => `${(r * 100).toFixed(2)}%`;

  const content = requiresManualTaxReview ? (
    <div className="space-y-1.5">
      <div className="flex gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-2">
        <AlertTriangle size={14} className="shrink-0 text-amber-600 mt-0.5" />
        <p className="text-[12px] text-amber-800 leading-snug">
          This is an interstate move. Tax must be determined manually.
        </p>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-slate">Subtotal</p>
        <p className="text-[12px] text-slate tabular-nums">{fmtCurrency(subtotal, symbol)}</p>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-slate">
          Tax Rate{taxState ? ` (${taxState})` : ''}
        </p>
        <p className="text-[12px] text-amber-600 font-medium">TBD</p>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-slate">Tax Amount</p>
        <p className="text-[12px] text-amber-600 font-medium">TBD</p>
      </div>
      <div className="flex items-center justify-between pt-1 border-t border-stone">
        <p className="text-[13px] font-semibold text-midnight">Total</p>
        <p className="text-[13px] font-semibold text-midnight tabular-nums">
          {fmtCurrency(subtotal, symbol)}
          <span className="text-[11px] text-slate font-normal ml-1">+ tax</span>
        </p>
      </div>
    </div>
  ) : (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-slate">Subtotal</p>
        <p className="text-[12px] text-slate tabular-nums">{fmtCurrency(subtotal, symbol)}</p>
      </div>
      {splitRates ? (
        <>
          <div className="flex items-center justify-between">
            <p className="text-[12px] text-slate">
              Labor Tax Rate{taxState ? ` (${taxState})` : ''}
            </p>
            <p className="text-[12px] text-slate tabular-nums">{pct(laborRate)}</p>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[12px] text-slate">
              Materials Tax Rate{taxState ? ` (${taxState})` : ''}
            </p>
            <p className="text-[12px] text-slate tabular-nums">{pct(matRate)}</p>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-between">
          <p className="text-[12px] text-slate">
            Tax Rate{taxState ? ` (${taxState})` : ''}
          </p>
          <p className="text-[12px] text-slate tabular-nums">{pct(laborRate)}</p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-slate">Tax Amount</p>
        <p className="text-[12px] text-slate tabular-nums">{fmtCurrency(taxAmount ?? 0, symbol)}</p>
      </div>
      {noTax && taxState && (
        <p className="text-[11px] text-slate italic">
          No sales tax applies in {taxState}
        </p>
      )}
      {splitRates && laborRate === 0 && taxState && (
        <p className="text-[11px] text-slate italic">
          Labor is not taxable in {taxState}; materials are.
        </p>
      )}
      {splitRates && matRate === 0 && taxState && (
        <p className="text-[11px] text-slate italic">
          Materials are not taxable in {taxState}.
        </p>
      )}
      <div className="flex items-center justify-between pt-1 border-t border-stone">
        <p className="text-[13px] font-semibold text-midnight">Total</p>
        <p className="text-[13px] font-semibold text-midnight tabular-nums">
          {fmtCurrency(total, symbol)}
        </p>
      </div>
    </div>
  );

  if (variant === 'card') {
    return (
      <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden px-4 py-3">
        {content}
      </div>
    );
  }

  return content;
}
